//==================================================================================================
// DynamicRemoteSelectBox
//
// Generic remote autocomplete select box.
//
// Responsibility:
// - Provides a server-driven Autocomplete (MSelect) that supports:
//   - Remote filtering (text input)
//   - Paging via skip/take with infinite scroll when the API indicates more results
//   - Pluggable DTO shapes through mappers (no assumptions about response fields)
//
// Key behavior:
// - Keeps typed filter text visible while the dropdown is open.
// - Shows the selected label when the dropdown is closed.
// - Uses a "take-latest" token so outdated responses do not overwrite newer results.
//==================================================================================================
import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import PropTypes from "prop-types";
import { LoadingContext, BufferService, MSelect } from "src/M_Components";
import { isFailurePayload } from "src/components/utils/apiResponse";

export default function DynamicRemoteSelectBox({
    label,
    serviceName,
    valueId,
    onChange,
    disabled,
    required,

    // paging/search config
    take = 25,
    debounceMs = 200,
    initialQuery = "",

    // required: query builder and response mappers
    buildQueryString,
    getItemsFromResponse,
    getMoreFromResponse,
    mapItemToOption,

    // optional: resolver for existing selections not present in current page
    resolveSelectedItem,

    // optional: parent persistence hooks
    onQueryChange,
    onOptionsChange,
}) {
    const ctx = useContext(LoadingContext);

    /**
     * BufferService wrapper for the target endpoint.
     * The caller passes a serviceName because different APIs can be exposed as different services.
     */
    const svc = useMemo(
        () => new BufferService(serviceName, ctx.DEFAULTURL),
        [serviceName, ctx.DEFAULTURL]
    );

    /**
     * UI state
     * - filterText: current typed filter
     * - options: mapped option list { value, label, raw }
     * - selected: current selection (same shape as options)
     */
    const [filterText, setFilterText] = useState(String(initialQuery || ""));
    const [options, setOptions] = useState([]);
    const [selected, setSelected] = useState(null);

    /**
     * Paging state
     * - metaMore: indicates if there are more records to fetch
     * - skip: current page offset
     */
    const [metaMore, setMetaMore] = useState(false);
    const [skip, setSkip] = useState(0);

    /**
     * Dropdown state
     * - isOpen: used only to decide whether input shows typed text or selected label
     */
    const [isOpen, setIsOpen] = useState(false);

    /**
     * Control refs
     * - debTimer: debounces the remote search
     * - unmountedRef: prevents state updates after unmount
     * - loadingMoreRef: prevents multiple concurrent "load more" calls
     * - queryTokenRef: take-latest token; incrementing invalidates in-flight requests
     */
    const debTimer = useRef(null);
    const unmountedRef = useRef(false);
    const loadingMoreRef = useRef(false);
    const queryTokenRef = useRef(0);

    /**
     * Persistence guards
     * - isUserTypingRef: ensures onQueryChange only fires for user typing, not prop sync
     * - lastEmittedQueryRef: prevents emitting identical query text repeatedly
     */
    const isUserTypingRef = useRef(false);
    const lastEmittedQueryRef = useRef(null);

    //==============================================================================================
    // Lifecycle cleanup
    //==============================================================================================
    useEffect(() => {
        return () => {
            unmountedRef.current = true;
            if (debTimer.current) clearTimeout(debTimer.current);
        };
    }, []);

    //==============================================================================================
    // Reset component state when the backing service changes
    // This prevents option carry-over between distinct endpoints/providers.
    //==============================================================================================
    useEffect(() => {
        setOptions([]);
        setSelected(null);
        setSkip(0);
        setMetaMore(false);
        queryTokenRef.current++;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serviceName]);

    //==============================================================================================
    // Emits query text to the parent only when the user is typing.
    //==============================================================================================
    const safeEmitQuery = useCallback(
        (q) => {
            if (typeof onQueryChange !== "function") return;
            if (!isUserTypingRef.current) return;
            if (lastEmittedQueryRef.current === q) return;

            lastEmittedQueryRef.current = q;
            onQueryChange(q);
        },
        [onQueryChange]
    );

    //==============================================================================================
    // Emits raw DTO rows (current options) to the parent when requested.
    //==============================================================================================
    const emitOptions = useCallback(
        (opts) => {
            if (typeof onOptionsChange !== "function") return;
            onOptionsChange(opts.map((o) => o?.raw).filter(Boolean));
        },
        [onOptionsChange]
    );

    //==============================================================================================
    // Sync initialQuery to the internal filter text without emitting events.
    // This path is designed for restoring per-tab snapshots.
    //==============================================================================================
    useEffect(() => {
        const next = String(initialQuery || "");
        if (next === filterText) return;

        setFilterText(next);

        // Invalidates any in-flight requests tied to the previous query.
        queryTokenRef.current++;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialQuery]);

    //==============================================================================================
    // fetchPage
    //
    // Behavior:
    // - Builds the remote query string using the caller-provided builder.
    // - Uses a take-latest token so only the most recent request can apply results.
    // - Supports appending results for infinite scroll.
    // - De-duplicates options by value to avoid duplicated items across pages.
    //==============================================================================================
    const fetchPage = useCallback(
        async ({ nextSkip, queryText, append }) => {
            if (disabled) return;

            // Take-latest: any newer request increments the token and invalidates older ones.
            const myToken = ++queryTokenRef.current;

            const q = String(queryText || "").trim();

            try {
                const qs = buildQueryString({ q, skip: nextSkip, take });
                const res = await svc.filter(qs);

                if (unmountedRef.current) return;
                if (myToken !== queryTokenRef.current) return;
                if (isFailurePayload(res)) return;

                const rawItems = getItemsFromResponse(res) || [];
                const items = rawItems.map(mapItemToOption);
                const more = !!getMoreFromResponse(res);

                setMetaMore(more);
                setSkip(nextSkip);

                setOptions((prev) => {
                    const merged = append ? [...prev, ...items] : items;

                    // De-dup by the option value (string comparison is the safest for mixed id types).
                    const seen = new Set();
                    const dedup = [];
                    for (const it of merged) {
                        const key = String(it?.value ?? "");
                        if (!key || seen.has(key)) continue;
                        seen.add(key);
                        dedup.push(it);
                    }

                    emitOptions(dedup);
                    return dedup;
                });
            } finally {
                loadingMoreRef.current = false;
            }
        },
        [
            disabled,
            buildQueryString,
            take,
            svc,
            getItemsFromResponse,
            getMoreFromResponse,
            mapItemToOption,
            emitOptions,
        ]
    );

    //==============================================================================================
    // Initial load
    // Loads the first page so the dropdown has data without requiring user input.
    //==============================================================================================
    useEffect(() => {
        if (disabled) return;
        fetchPage({ nextSkip: 0, queryText: filterText, append: false });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disabled]);

    //==============================================================================================
    // Resolves the selected label when valueId exists but is not in the current option page.
    // This supports edit/view scenarios where the selected value must be displayed immediately.
    //==============================================================================================
    useEffect(() => {
        if (!valueId) {
            setSelected(null);
            return;
        }

        const hit = options.find((o) => String(o?.value) === String(valueId));
        if (hit) {
            setSelected(hit);
            return;
        }

        if (typeof resolveSelectedItem !== "function") {
            setSelected({ value: valueId, label: String(valueId), raw: null });
            return;
        }

        let cancelled = false;
        (async () => {
            try {
                const raw = await resolveSelectedItem(svc, valueId);
                if (cancelled || unmountedRef.current) return;

                if (!raw) {
                    setSelected({ value: valueId, label: String(valueId), raw: null });
                    return;
                }

                setSelected(mapItemToOption(raw));
            } catch {
                if (!cancelled && !unmountedRef.current) {
                    setSelected({ value: valueId, label: String(valueId), raw: null });
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [valueId, options, resolveSelectedItem, svc, mapItemToOption]);

    //==============================================================================================
    // Debounced search input
    // - Updates filterText
    // - Emits query persistence (optional)
    // - Resets paging to skip=0 and replaces options
    //==============================================================================================
    const onUserInput = useCallback(
        (text) => {
            isUserTypingRef.current = true;

            const next = String(text || "");
            setFilterText(next);
            safeEmitQuery(next);

            if (debTimer.current) clearTimeout(debTimer.current);
            debTimer.current = setTimeout(() => {
                fetchPage({ nextSkip: 0, queryText: next, append: false });
            }, debounceMs);
        },
        [safeEmitQuery, fetchPage, debounceMs]
    );

    //==============================================================================================
    // Infinite scroll handler
    // Loads the next page when the user scrolls near the bottom and meta.more is true.
    //==============================================================================================
    const handleListScroll = useCallback(
        (e) => {
            if (!metaMore) return;
            if (loadingMoreRef.current) return;

            const el = e?.currentTarget;
            if (!el) return;

            const nearBottom =
                el.scrollTop + el.clientHeight >= el.scrollHeight - 24;

            if (!nearBottom) return;

            loadingMoreRef.current = true;
            fetchPage({ nextSkip: skip + take, queryText: filterText, append: true });
        },
        [metaMore, skip, take, filterText, fetchPage]
    );

    //==============================================================================================
    // Input text shown
    // - When open: shows current filterText
    // - When closed: shows selected label so the chosen value remains visible
    //==============================================================================================
    const inputValueShown = useMemo(() => {
        if (isOpen) return filterText;
        return selected?.label ?? filterText;
    }, [isOpen, filterText, selected?.label]);

    return (
        <MSelect
            label={label}
            autocomplete
            required={!!required}
            disabled={!!disabled}
            options={options}
            value={selected}
            inputValue={inputValueShown}
            clearOnBlur={false}
            onOpen={() => setIsOpen(true)}
            onClose={() => {
                setIsOpen(false);
                isUserTypingRef.current = false;
            }}
            filterOptions={(xs) => xs}
            ListboxProps={{
                style: { maxHeight: 196, overflow: "auto" },
                onScroll: handleListScroll,
            }}
            onInputChange={(_, v, reason) => {
                if (reason === "input") onUserInput(v || "");
                if (reason === "clear") onUserInput("");
            }}
            onChangeValue={(sel) => {
                const next = sel || null;

                // Clear selection path: resets selection and reloads the first page unfiltered.
                if (!next) {
                    setSelected(null);
                    setFilterText("");
                    if (typeof onQueryChange === "function") onQueryChange("");
                    onChange?.(null);

                    fetchPage({ nextSkip: 0, queryText: "", append: false });
                    return;
                }

                // Selection path: stores the option object and notifies the parent.
                setSelected(next);
                onChange?.(next);
            }}
        />
    );
}

DynamicRemoteSelectBox.propTypes = {
    label: PropTypes.string,
    serviceName: PropTypes.string.isRequired,
    valueId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    required: PropTypes.bool,
    take: PropTypes.number,
    debounceMs: PropTypes.number,
    initialQuery: PropTypes.string,

    buildQueryString: PropTypes.func.isRequired,
    getItemsFromResponse: PropTypes.func.isRequired,
    getMoreFromResponse: PropTypes.func.isRequired,
    mapItemToOption: PropTypes.func.isRequired,
    resolveSelectedItem: PropTypes.func,

    onQueryChange: PropTypes.func,
    onOptionsChange: PropTypes.func,
};